import { API_ROOT_URL, BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  BulkAttachExistingPayload,
  BulkAttachExistingResult,
  BulkIdsPayload,
  BulkInvitationRemindersResponse,
  BulkRefreshCalculationResponse,
  CollaborationCommunicationListResponse,
  CollaborationMapResponse,
  CompanyChainDetailResponse,
  CompanyChainGraphResponse,
  CompanyChainListResponse,
  CompanyChainRow,
  CompanyLinkPayload,
  CountryOverviewResponse,
  CreateSupplyChainExportsResponse,
  CreateSupplyChainPayload,
  CreateSupplyChainResponse,
  CriticalSupplier,
  DescendantCollaboratorListResponse,
  FacilityProcessOverviewResponse,
  GlobalDiagramResponse,
  InsightsHistoryResponse,
  InvitableCompaniesResponse,
  InviteCollaboratorPayload,
  LinkCompaniesPayload,
  ListCollaborationCommunicationsPayload,
  ListDescendantCollaboratorsPayload,
  MappedCompaniesListResponse,
  ReplaceCompanyPayload,
  ReuseCompanyChainPayload,
  SendInvitationReminderPayload,
  SendInvitationReminderResponse,
  SupplierDetailsResponse,
  SupplierStatistics,
  UnmappedCompaniesResponse,
  UpdateCollaborationStatusPayload,
  UpdateNodeFacilityProcessesPayload,
  UpdateSupplyChainMetadataPayload,
  UpdateSupplyChainStatusPayload,
} from './types';

const SCM_BASE_URL = BACKEND_URL;

// `reply.apiSuccess` wraps the payload as `{ metadata, data }`; `reply.paginate`
// emits `{ data, meta }`, which is what legacy already sent — so only the
// `metadata` envelope is unwrapped.
interface ApiSuccessEnvelope<T> {
  metadata: unknown;
  data: T;
}

function isEnvelope<T>(body: unknown): body is ApiSuccessEnvelope<T> {
  return typeof body === 'object' && body !== null && 'metadata' in body;
}

function unwrapEnvelope<T>(body: unknown): T {
  return isEnvelope<T>(body) ? body.data : (body as T);
}

async function scmFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  return unwrapEnvelope<T>(await apiFetch<unknown>(path, init, SCM_BASE_URL));
}

export async function fetchCompanyChainGraph(
  collaborationId: string,
): Promise<CompanyChainGraphResponse> {
  return scmFetch<CompanyChainGraphResponse>(
    `/supplychain-mappings/collaborations/${collaborationId}/expand-all`,
    {},
  );
}

export async function fetchCompanyChainDetail(
  collaborationId: string,
): Promise<CompanyChainDetailResponse> {
  return scmFetch<CompanyChainDetailResponse>(
    `/supplychain-mappings/collaborations/${collaborationId}`,
    {},
  );
}

export async function fetchCompanyChains(
  qs: string,
): Promise<CompanyChainListResponse> {
  return scmFetch<CompanyChainListResponse>(
    `/supplychain-mappings/collaborations?${qs}`,
    {},
  );
}

export async function bulkArchiveCompanyChains(
  body: BulkIdsPayload,
): Promise<unknown> {
  return scmFetch<unknown>('/supplychain-mappings/bulk-archive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function bulkUnarchiveCompanyChains(
  body: BulkIdsPayload,
): Promise<unknown> {
  return scmFetch<unknown>('/supplychain-mappings/bulk-unarchive', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function bulkRefreshCompanyChainsCalculation(
  body: BulkIdsPayload,
): Promise<BulkRefreshCalculationResponse> {
  return scmFetch<BulkRefreshCalculationResponse>(
    '/supplychain-mappings/bulk-refresh-calculation',
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function bulkExportCompanyChains(
  body: BulkIdsPayload,
): Promise<CreateSupplyChainExportsResponse> {
  return scmFetch<CreateSupplyChainExportsResponse>(
    '/supplychain-mappings/export',
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function createCompanySupplyChain(
  body: CreateSupplyChainPayload,
): Promise<CreateSupplyChainResponse> {
  return scmFetch<CreateSupplyChainResponse>('/supplychain-mappings', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function updateSupplyChainMetadata(
  collaborationId: string,
  body: UpdateSupplyChainMetadataPayload,
): Promise<unknown> {
  return scmFetch<unknown>(
    `/supplychain-mappings/collaborations/${collaborationId}/metadata`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function fetchUnmappedCompaniesForNode(
  nodeId: string,
  qs: string,
): Promise<UnmappedCompaniesResponse> {
  return scmFetch<UnmappedCompaniesResponse>(
    `/supplychain-mappings/nodes/${nodeId}/unmapped-company-profiles?${qs}`,
    {},
  );
}

export async function linkCompaniesIntoSupplyChain(
  collaborationId: string,
  body: LinkCompaniesPayload,
): Promise<unknown> {
  return scmFetch<unknown>(
    `/supplychain-mappings/collaborations/${collaborationId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function updateNodeFacilityProcesses(
  nodeId: string,
  body: UpdateNodeFacilityProcessesPayload,
): Promise<unknown> {
  return scmFetch<unknown>(`/supplychain-mappings/nodes/${nodeId}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function deleteSupplyChainNode(nodeId: string): Promise<unknown> {
  return scmFetch<unknown>(`/supplychain-mappings/nodes/${nodeId}`, {
    method: 'DELETE',
  });
}

export async function removeSupplyChainCollaboration(
  collaborationId: string,
): Promise<void> {
  return scmFetch<void>(
    `/supplychain-mappings/collaborations/${collaborationId}`,
    { method: 'DELETE' },
  );
}

export async function sendInvitationReminder(
  collaborationId: string,
  body: SendInvitationReminderPayload,
): Promise<SendInvitationReminderResponse> {
  return scmFetch<SendInvitationReminderResponse>(
    `/supplychain-mappings/collaborations/${collaborationId}/invitation-reminders`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function bulkSendInvitationReminders(
  body: BulkIdsPayload,
): Promise<BulkInvitationRemindersResponse> {
  return scmFetch<BulkInvitationRemindersResponse>(
    '/supplychain-mappings/bulk-invitation-reminders',
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function updateSupplyChainStatus(
  collaborationId: string,
  body: UpdateSupplyChainStatusPayload,
): Promise<unknown> {
  return scmFetch<unknown>(
    `/supplychain-mappings/collaborations/${collaborationId}/status`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

// Lives on the main API gateway, not on cascade-map-service.
export async function connectCompanyAsSupplier(
  body: CompanyLinkPayload,
): Promise<unknown> {
  return apiFetch<unknown>(
    '/api/companies/link',
    { method: 'POST', body: JSON.stringify(body) },
    API_ROOT_URL,
  );
}

export async function inviteSupplyChainCollaborators(
  body: InviteCollaboratorPayload,
): Promise<unknown> {
  return scmFetch<unknown>('/supplychain-mappings/collaborators', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function bulkDuplicateCompanyChains(
  collaborationIds: string[],
): Promise<{ data: CompanyChainRow[] }> {
  return scmFetch<{ data: CompanyChainRow[] }>(
    '/supplychain-mappings/bulk-duplicate',
    { method: 'POST', body: JSON.stringify({ collaborationIds }) },
  );
}

export async function bulkAttachExisting(
  body: BulkAttachExistingPayload,
): Promise<BulkAttachExistingResult> {
  return scmFetch<BulkAttachExistingResult>(
    '/supplychain-mappings/bulk-attach-existing',
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function reuseCompanyChain(
  body: ReuseCompanyChainPayload,
): Promise<CompanyChainGraphResponse> {
  return scmFetch<CompanyChainGraphResponse>('/supplychain-mappings/reuse', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function fetchInvitableCompanies(
  collaborationId: string,
  qs: string,
): Promise<InvitableCompaniesResponse> {
  return scmFetch<InvitableCompaniesResponse>(
    `/supplychain-mappings/${collaborationId}/companies?${qs}`,
    {},
  );
}

// ── Insights ─────────────────────────────────────────────────────────────────

export async function fetchSupplierStatistics(): Promise<SupplierStatistics> {
  return scmFetch<SupplierStatistics>(
    '/supplychain-mappings/supplier-statistics/',
    {},
  );
}

export async function fetchInsightsHistory(
  qs: string,
): Promise<InsightsHistoryResponse> {
  return scmFetch<InsightsHistoryResponse>(
    `/supplychain-mappings/history?${qs}`,
    {},
  );
}

export async function fetchMappedCompanies(
  qs: string,
): Promise<MappedCompaniesListResponse> {
  return scmFetch<MappedCompaniesListResponse>(
    `/supplychain-mappings/mapped-companies?${qs}`,
    {},
  );
}

export async function fetchSupplierDetails(
  companyId: string,
  filter?: string,
): Promise<SupplierDetailsResponse> {
  const qs = filter ? `?filter=${encodeURIComponent(filter)}` : '';
  return scmFetch<SupplierDetailsResponse>(
    `/supplychain-mappings/insights/suppliers/${companyId}${qs}`,
    {},
  );
}

export async function fetchCollaborationMap(
  collaborationId: string,
): Promise<CollaborationMapResponse> {
  return scmFetch<CollaborationMapResponse>(
    `/supplychain-mappings/collaborations/${collaborationId}/map`,
    {},
  );
}

export async function fetchCollaboratorCompanies(
  qs: string,
): Promise<MappedCompaniesListResponse> {
  return scmFetch<MappedCompaniesListResponse>(
    `/supplychain-mappings/collaborators?${qs}`,
    {},
  );
}

export async function fetchCriticalSuppliers(): Promise<CriticalSupplier[]> {
  return scmFetch<CriticalSupplier[]>(
    '/supplychain-mappings/critical-suppliers-statistics',
    {},
  );
}

export async function fetchFacilityProcessOverview(): Promise<FacilityProcessOverviewResponse> {
  return scmFetch<FacilityProcessOverviewResponse>(
    '/supplychain-mappings/facility-process-overview',
    {},
  );
}

export async function fetchCountryOverview(): Promise<CountryOverviewResponse> {
  return scmFetch<CountryOverviewResponse>(
    '/supplychain-mappings/country-overview',
    {},
  );
}

export async function fetchGlobalDiagram(
  qs: string,
): Promise<GlobalDiagramResponse> {
  return scmFetch<GlobalDiagramResponse>(
    `/supplychain-mappings/insights/global-diagram/${qs ? `?${qs}` : ''}`,
    {},
  );
}

export async function replaceSupplyChainNode(
  nodeId: string,
  body: ReplaceCompanyPayload,
): Promise<void> {
  return scmFetch<void>(`/supplychain-mappings/nodes/${nodeId}/replace`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export async function acceptCollaborationInvitation(
  collaborationId: string,
  body: { collaborationName?: string },
): Promise<void> {
  return scmFetch<void>(
    `/supplychain-mappings/collaborations/${collaborationId}/accept`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function updateCollaborationStatus(
  collaborationId: string,
  body: UpdateCollaborationStatusPayload,
): Promise<void> {
  return scmFetch<void>(
    `/supplychain-mappings/collaborations/${collaborationId}/collaboration-status`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
}

export async function fetchDescendantCollaborators(
  collaborationId: string,
  body: ListDescendantCollaboratorsPayload,
): Promise<DescendantCollaboratorListResponse> {
  return scmFetch<DescendantCollaboratorListResponse>(
    `/supplychain-mappings/collaborations/${collaborationId}/collaborators/list`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

export async function fetchCollaborationCommunications(
  collaborationId: string,
  body: ListCollaborationCommunicationsPayload,
): Promise<CollaborationCommunicationListResponse> {
  return scmFetch<CollaborationCommunicationListResponse>(
    `/supplychain-mappings/collaborations/${collaborationId}/communications/list`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}
